// #1266: a person's public profile in the caller's enterprise, as `GET|PATCH /v1/users/:id/profile` serve it.
// Presentation only: there is no grant or ownership field in either direction, so nothing written here can
// widen what anybody may reach.

import { UsersApiError } from '@/api-users'

export type ProfileLinkLabel = 'LinkedIn' | 'X' | 'Website'

/** The labels a profile link may carry, in the order the form offers them. One link per label. */
export const PROFILE_LINK_LABELS: readonly ProfileLinkLabel[] = ['LinkedIn', 'X', 'Website']

export type ProfileLink = { label: ProfileLinkLabel; url: string }

export type PersonProfile = {
    jobTitle: string | null
    biography: string | null
    /** In the order a card shows them. */
    socialLinks: ProfileLink[]
    /** Fail-closed: false until somebody lists the person. */
    publiclyListed: boolean
}

export type ProfilePatch = {
    jobTitle?: string | null
    biography?: string | null
    socialLinks?: ProfileLink[] | null
    publiclyListed?: boolean
}

/** The API's code and sentence for a refusal, so the form can show what was wrong rather than a number. */
async function refusal(response: Response): Promise<UsersApiError> {
    try {
        const body = (await response.json()) as { error?: unknown; message?: unknown }
        const nested =
            typeof body.message === 'object' && body.message !== null
                ? (body.message as { error?: unknown; message?: unknown })
                : undefined
        const code = [nested?.error, body.error].find((value): value is string => typeof value === 'string')
        const detail = [nested?.message, body.message].find(
            (value): value is string => typeof value === 'string' && value !== '',
        )
        return new UsersApiError(code ?? String(response.status), detail ?? null)
    } catch {
        return new UsersApiError(String(response.status), null)
    }
}

async function call(path: string, init: RequestInit = {}): Promise<PersonProfile> {
    const response = await fetch(path, { ...init, headers: { 'Content-Type': 'application/json' } })
    if (!response.ok) {
        throw await refusal(response)
    }
    return (await response.json()) as PersonProfile
}

function profilePath(id: string): string {
    return `/v1/users/${encodeURIComponent(id)}/profile`
}

export const profilesApi = {
    /** One person's profile in the caller's enterprise: anybody's for an owner, otherwise only their own. */
    profile: (id: string): Promise<PersonProfile> => call(profilePath(id)),

    /** Writes the given profile fields, and only those; refused with a `UsersApiError` whose `detail` says why. */
    updateProfile: (id: string, patch: ProfilePatch): Promise<PersonProfile> =>
        call(profilePath(id), { method: 'PATCH', body: JSON.stringify(patch) }),
}
